"use client";

type FileNameProps = {
  text: string;
  /**
   * Number of characters before the extension to show
   */
  padding?: number;
  /**
   * Whether or not a text string is a filename with an extension
   */
  hasExtension?: boolean;
};

type TruncatedFileNameExtension = {
  initial: string;
  remainder: string;
};

/**
 * The entry point for truncation from `TextWithTruncation` - uses the
 * `hasExtension` property to determine which truncation is applied.
 *
 * @param {Object} props FileNameProps used for extracting `initial` and
 *    `remainder` truncation values.
 * @returns {Object} An TruncatedFileNameExtension object with `initial` and
 *    `remainder` truncation strings.
 */
export const truncateText = ({
  text,
  padding,
  hasExtension,
}: FileNameProps): TruncatedFileNameExtension => {
  // use `separateFileNameExtension` for file names (has extension)
  if (hasExtension) {
    return separateFileNameExtension({ text, padding });
  }

  // if not a file (no extension), and a padding value is specified
  if (padding && text.length > padding) {
    const end = text.slice(-padding);
    const beginning = text.split(end).shift();

    return {
      initial: beginning,
      remainder: end,
    };
  }

  // default when no padding or hasExtension props are defined
  return {
    initial: text,
    remainder: null,
  };
};

/**
 * A specific utility for separating filenames and extensions.
 *
 * @param {Object} props FileNameProps used for extracting `initial` and
 *    `remainder` truncation values.
 * @returns {Object} An TruncatedFileNameExtension object with `initial` and
 *    `remainder` truncation strings.
 */
export const separateFileNameExtension = ({
  text,
  padding = 0,
}: FileNameProps): TruncatedFileNameExtension => {
  const truncText = text.split('/').pop();
  const dotIndex = truncText.lastIndexOf('.');

  // early return if no text extension present
  if (dotIndex <= 0) {
    return {
      initial: truncText,
      remainder: null,
    };
  }

  /**
   * ensure there is enough text with provided padding, otherwise return
   * substring from the last index of `.`
   */
  return {
    initial: truncText.substr(
      0,
      dotIndex - padding <= 0 ? dotIndex : dotIndex - padding
    ),
    remainder: truncText.substr(
      dotIndex - padding <= 0 ? dotIndex : dotIndex - padding,
      truncText.length
    ),
  };
};

/**
 * Prevents quirky clipboard data when selecting text across HTML elements by
 * removing line feed and carriage return characters.
 */
export const normalizeClipboardData = (e: React.ClipboardEvent) => {
  e.preventDefault();

  const normalizedSelectedText = window
    .getSelection()
    .toString()
    .replace(/[\n\r]+/g, '');

  e.clipboardData.setData('text/plain', normalizedSelectedText);
};
